/**
 * Converte o token de um termo de vocabulário no valor que o catálogo indexa.
 *
 * `zope.schema.vocabulary.SimpleTerm` escapa todo caractere não-ASCII do token
 * (`token.encode('ascii', 'backslashreplace')`), mesmo quando o vocabulário é
 * construído com `SimpleTerm(value=t, token=t, title=t)`. O tema
 * "Direitos do Cidadão" chega então ao frontend como:
 *
 *     { title: 'Direitos do Cidadão', token: 'Direitos do Cidad\\xe3o' }
 *
 * O catálogo, porém, indexa o *valor* do termo — o texto acentuado. Consultar
 * o índice `tema` com o token não devolve nada, e são 9 dos 19 temas que têm
 * acento ou cedilha. É por isso que o bloco "Notícias por tema" e o filtro da
 * listagem funcionavam em "Meio Ambiente" (sem acento, token == valor) e
 * vinham vazios em "Direitos do Cidadão".
 *
 * Esta função desfaz exatamente aquele escape, nas três formas que o
 * `backslashreplace` emite (`\xNN`, `\uNNNN`, `\UNNNNNNNN`). É idempotente:
 * num token ASCII, ou num valor já resolvido, não há sequência para trocar e o
 * texto volta intacto — então pode ser aplicada sem saber qual dos dois chegou.
 */
const PYTHON_ESCAPE =
  /\\x([0-9a-fA-F]{2})|\\u([0-9a-fA-F]{4})|\\U([0-9a-fA-F]{8})/g;

export const decodeVocabularyToken = <T extends string | undefined>(
  token: T,
): T => {
  if (!token) {
    return token;
  }

  return token.replace(PYTHON_ESCAPE, (match, hex2, hex4, hex8) => {
    const codePoint = Number.parseInt(hex2 || hex4 || hex8, 16);

    // Fora do intervalo válido o escape não era um escape — devolve como veio.
    return Number.isNaN(codePoint) || codePoint > 0x10ffff
      ? match
      : String.fromCodePoint(codePoint);
  }) as T;
};

export default decodeVocabularyToken;
